import type { ReactNode } from "react";
import { Box, Tooltip, Typography, useMediaQuery } from "@mui/material";
import type { Theme } from "@mui/material";
import { colorReasonArray } from "../../../utils/methods/eventUtils";
import { isActiveRole, isUserRole } from "../../../utils/methods/storeFunc";
import { USER_ROLES } from "../../../staticData/userRoles";
import { EVENT_BAR_LEGEND } from "./calendarStatusColors";
import type { BarLook } from "./calendarStatusColors";
import EventTypeBadge from "./EventTypeBadge";
import { SEVERITY_COLORS } from "../overview/boardStyles";
import type { DashboardView } from "../_hooks/useDashboardView";
import { FINANCE_STATUS_OPTIONS } from "../finance/financialDashboard.utils";

type Item = { key: string; swatch: ReactNode; label: string };

const swatchSx = { height: "30px", aspectRatio: 1, borderRadius: "0.5rem", flexShrink: 0 };

function BarSwatch({ look }: { look: BarLook }) {
  return <Box sx={{ ...swatchSx, backgroundColor: look.bg, border: look.border ?? "none" }} />;
}

function ColorSwatch({ color }: { color: string }) {
  return <Box sx={{ ...swatchSx, backgroundColor: color }} />;
}

/**
 * "Colors?" next to the dashboard title, same look as the table's color
 * explanation, for the three views: bar colors (calendar / overview),
 * attention colors (overview), stage tags (table) and the admin status strips
 * (table / calendar). Replaces the legend chips under the calendar and the timeline.
 */
export default function DashboardColorsLegend({ view }: { view: DashboardView }) {
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  if (isSmallScreen || isActiveRole(USER_ROLES.CLIENT)) return null;
  const isAdmin = isUserRole(USER_ROLES.SUPERADMIN, USER_ROLES.ADMIN);

  const sections: Array<{ title: string; items: Item[] }> = [];
  if (view === "finance") {
    sections.push({ title: "Invoice sending", items: Object.entries(FINANCE_STATUS_OPTIONS).map(([key, option]) => ({
      key, label: option.label, swatch: <ColorSwatch color={option.color} />,
    })) });
  }
  if (view === "calendar" || view === "overview") {
    sections.push({
      title: "Show colors",
      items: EVENT_BAR_LEGEND.map(({ label, look }) => ({
        key: label,
        label,
        swatch: <BarSwatch look={look} />,
      })),
    });
  }
  if (view === "overview") {
    sections.push({
      title: "Needs attention",
      items: [
        { key: "critical", label: "Running now or starts within 7 days", swatch: <ColorSwatch color={SEVERITY_COLORS.critical} /> },
        { key: "warning", label: "Starts within 14 days", swatch: <ColorSwatch color={SEVERITY_COLORS.warning} /> },
        { key: "info", label: "Starts within 30 days", swatch: <ColorSwatch color={SEVERITY_COLORS.info} /> },
      ],
    });
  }
  if (view === "overview" && isAdmin) {
    sections.push({
      title: "To close (payments)",
      items: [
        { key: "late", label: "Something is late (invoice, client or crew)", swatch: <ColorSwatch color={SEVERITY_COLORS.critical} /> },
        { key: "waiting", label: "Waiting for payment, still on time", swatch: <ColorSwatch color="#D175B6" /> },
        { key: "closed", label: "Finalized: invoiced, client paid, crew paid", swatch: <ColorSwatch color="#00EEC5" /> },
      ],
    });
  }
  if (view === "table") {
    sections.push({
      title: "Stage tags",
      items: [
        { key: "draft", label: "Started but never submitted", swatch: <EventTypeBadge lifecycle="draft" /> },
        { key: "newRequest", label: "Submitted, estimate not sent yet", swatch: <EventTypeBadge lifecycle="newRequest" /> },
        { key: "pendingApproval", label: "Estimate sent, waiting for approval", swatch: <EventTypeBadge lifecycle="pendingApproval" /> },
      ],
    });
  }
  if ((view === "table" || view === "calendar") && isAdmin) {
    sections.push({
      title: "Status strips",
      items: colorReasonArray.map((item, i) => ({
        key: `strip-${i}`,
        label: item?.reason,
        swatch: <ColorSwatch color={item?.color} />,
      })),
    });
  }
  if (!sections.length) return null;

  return (
    <Tooltip
      arrow
      title={
        <Box sx={{ display: "flex", flexDirection: "column", gap: "0.75rem", padding: "0.75rem" }}>
          {sections.map((section) => (
            <Box key={section.title} sx={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
              <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#D175B6" }}>
                {section.title}
              </Typography>
              {section.items.map((item) => (
                <Box key={item.key} sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  {item.swatch}
                  <Typography>{item.label}</Typography>
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      }
    >
      <Typography tabIndex={0} role="button" aria-label="Explain dashboard colors" sx={{ display: "inline", cursor: "pointer", mx: 1, padding: "0.25rem 0.5rem" }}>
        {"Colors?".split("").map((char, index) => (
          <span key={index} style={{ color: colorReasonArray[index % colorReasonArray.length]?.color }}>
            {char}
          </span>
        ))}
      </Typography>
    </Tooltip>
  );
}
